import React, { useState, useEffect } from 'react';
import { X, Loader2, Mail, RefreshCw, Search, UserSearch, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Client {
  id: number;
  nombre: string;
  cuil_cuit: string;
  condicion_fiscal: string;
  email?: string;
  domicilio?: string;
}

interface SaleItem {
  variantId: number;
  productName: string;
  variantName: string;
  quantity: number;
  price: number;
  discounts?: {
    discountId: number;
    discountName: string;
    discountValue: number;
    discountType: string;
    minQuantity?: number | null;
    maxQuantity?: number | null;
    calculoDescuento: string;
    valorDescuento: number;
  }[];
}

interface SaleFinalizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedClient: Client | null;
  selectedPaymentMethod: {
    id: number;
    metodo_de_pago: string;
    comision: number;
  } | null;
  invoiceType: 'A' | 'B' | 'CF' | null;
  receiptType: 'comprobante' | 'factura';
  items: SaleItem[];
  totals: {
    subtotal: number;
    totalDiscounts?: number;
    subtotalAfterDiscounts?: number;
    iva: number;
    neto: number;
    commission: number;
    total: number;
  };
  companyConfig: {
    condicion_fiscal: string;
    monto_min_facturacion_cash: number;
    monto_min_facturacion_card?: number;
  } | null;
  localId: number | null;
  cajaId: number | null;
  onSuccess: () => void;
}

export const SaleFinalizationModal: React.FC<SaleFinalizationModalProps> = ({
  isOpen,
  onClose,
  selectedClient,
  selectedPaymentMethod,
  items,
  totals,
  companyConfig,
  localId,
  cajaId,
  invoiceType,
  receiptType,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientSearch, setClientSearch] = useState('');
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedCondicionIva, setSelectedCondicionIva] = useState<'RI' | 'Exento'>('RI');
  const [newClient, setNewClient] = useState<Partial<Client>>({});
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  /* facturaId is used for future functionality like sending/generating invoices
     Note: Keeping this state variable even though currently unused as it's part of the application's planned features */
  const [facturaId, setFacturaId] = useState<number | null>(null);

  const [showClientSearchModal, setShowClientSearchModal] = useState(false);
  const [bypassClientInput, setBypassClientInput] = useState(false);
  const [emailInputValue, setEmailInputValue] = useState('');
  const [nameInputValue, setNameInputValue] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  // Local state to track the selected client within the component
  const [localSelectedClient, setLocalSelectedClient] = useState<Client | null>(selectedClient);
  // We'll use bypassClientInput to track bypass state directly

  // Initialize the local state with the prop value
  useEffect(() => {
    setLocalSelectedClient(selectedClient);
  }, [selectedClient]);
  
  // Auto-process sale when the modal opens and bypass conditions are met
  useEffect(() => {
    if (isOpen && bypassClientInput) {
      console.log('Auto-processing sale with bypass conditions met');
      // Small delay just to ensure state is fully updated
      const timer = setTimeout(() => {
        handleFinalizeSale();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen, bypassClientInput]);
  
  // Check if conditions for bypass are met
  useEffect(() => {
    // Set default to false - no bypass
    let shouldBypass = false;
    
    // Only proceed if we have company configuration
    if (!companyConfig) {
      console.log('No companyConfig, bypass conditions not checked');
      setBypassClientInput(false);
      return;
    }
    
    // Detailed logging of input values
    console.log('Input values for bypass check:', {
      invoiceType,
      receiptType, 
      paymentMethod: selectedPaymentMethod?.metodo_de_pago,
      totalAmount: totals.total,
      minCashAmount: companyConfig.monto_min_facturacion_cash,
      minCardAmount: companyConfig.monto_min_facturacion_card
    });
    
    // Ensure the client type is Consumidor Final
    if (invoiceType !== 'CF') {
      console.log('Not a Consumidor Final sale, bypass disabled');
      setBypassClientInput(false);
      return;
    }
    
    // Check receipt type is valid for bypass
    if (receiptType !== 'comprobante' && receiptType !== 'factura') {
      console.log('Receipt type not eligible for bypass:', receiptType);
      setBypassClientInput(false);
      return;
    }
    
    // Now check the payment method and amount conditions
    const isPagoEfectivo = selectedPaymentMethod?.metodo_de_pago === 'Efectivo';
    const totalAmount = Number(totals.total) || 0;
    const minCashAmount = Number(companyConfig.monto_min_facturacion_cash) || 0;
    const minCardAmount = Number(companyConfig.monto_min_facturacion_card) || 0;
    
    console.log('Converted numeric values:', {
      totalAmount,
      minCashAmount,
      minCardAmount,
      isPagoEfectivo
    });
    
    // Check if any minimum amount is set - if both are 0, bypass is disabled
    if (minCashAmount <= 0 && minCardAmount <= 0) {
      console.log('Both minimum amounts are zero or negative, bypass disabled');
      setBypassClientInput(false);
      return;
    }
    
    // Cash payment condition
    if (isPagoEfectivo) {
      if (minCashAmount > 0 && totalAmount <= minCashAmount) {
        console.log(`Cash payment below threshold: ${totalAmount} <= ${minCashAmount}`);
        shouldBypass = true;
      } else {
        console.log(`Cash payment above threshold or threshold not set: ${totalAmount} > ${minCashAmount}`);
      }
    } 
    // Non-cash payment condition
    else {
      if (minCardAmount > 0 && totalAmount <= minCardAmount) {
        console.log(`Card payment below threshold: ${totalAmount} <= ${minCardAmount}`);
        shouldBypass = true;
      } else {
        console.log(`Card payment above threshold or threshold not set: ${totalAmount} > ${minCardAmount}`);
      }
    }
    
    console.log('Final bypass decision:', shouldBypass);
    setBypassClientInput(shouldBypass);
  }, [invoiceType, receiptType, selectedPaymentMethod, totals.total, companyConfig]);
  
  useEffect(() => {
    if (clientSearch.trim()) {
      searchClients(clientSearch);
    } else {
      setClients([]);
    }
  }, [clientSearch, invoiceType, selectedCondicionIva]);

  const searchClients = async (search: string, modal = false) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .maybeSingle();

      if (!profile?.company_id) return;

      let query = supabase
        .schema(profile.company_id)
        .from('clientes')
        .select();
      
      if (search.trim()) {
        query = query.or(`nombre.ilike.%${search}%,cuil_cuit.cast.text.ilike.%${search}%`);
      }

      // Add condition based on invoice type (for both modal and inline search)
      if (invoiceType === 'A') {
        query = query.eq('condicion_fiscal', selectedCondicionIva);
      } else if (invoiceType === 'B') {
        query = query.eq('condicion_fiscal', 'M');
      } else if (invoiceType === 'CF') {
        query = query.eq('condicion_fiscal', 'CF');
      }

      // If searching in modal, we want more results
      const limit = modal ? 50 : 5;
      query = query.limit(limit);

      const { data } = await query;
      setClients(data || []);
    } catch (err) {
      console.error('Error searching clients:', err);
    }
  };
  
  // Initial search for the modal when it opens
  useEffect(() => {
    if (showClientSearchModal) {
      searchClients('', true);
    }
  }, [showClientSearchModal]);

  const handleCreateClient = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');

      const { data: client, error: insertError } = await supabase
        .schema(profile.company_id)
        .from('clientes')
        .insert({
          ...newClient,
          condicion_fiscal: invoiceType === 'A' ? selectedCondicionIva :
                          invoiceType === 'B' ? 'M' : 'CF'
        })
        .select()
        .single();

      if (insertError) throw insertError;

      return client;
    } catch (err) {
      console.error('Error creating client:', err);
      throw err;
    }
  };

  // Helper function to round to two decimal places
  const roundToTwoDecimals = (num: number | null | undefined): number | null => {
    if (num === null || num === undefined) {
      return null;
    }
    // Handle potential floating point inaccuracies with Math.round
    return Math.round((num + Number.EPSILON) * 100) / 100;
  };

  // Email validation function
  const validateEmail = (email: string) => {
    const regex = /^\S+@\S+\.\S+$/;
    return regex.test(email);
  };

  // Handle email input change with validation
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmailInputValue(value);
    setIsEmailValid(validateEmail(value));
  };

  // Handle email sending
  const handleEmailSend = async () => {
    if (!isEmailValid || !ventaId) return;
    
    try {
      setEmailSending(true);
      console.log('Sending email, bypass status:', bypassClientInput);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');
      
      // Different behavior based on whether bypass logic was followed
      if (bypassClientInput) {
        console.log('Bypass mode: Creating new client or using existing client by email');
        // For bypassed sales: Check if client with this email exists
        const { data: existingClients } = await supabase
          .schema(profile.company_id)
          .from('clientes')
          .select('id')
          .eq('email', emailInputValue)
          .limit(1);
        
        let clientId;
        
        if (existingClients && existingClients.length > 0) {
          // Use existing client
          clientId = existingClients[0].id;
          console.log('Using existing client:', clientId);
        } else {
          // Create new client
          const { data: newClient } = await supabase
            .schema(profile.company_id)
            .from('clientes')
            .insert({
              nombre: nameInputValue || null,
              email: emailInputValue,
              condicion_fiscal: 'CF'
            })
            .select('id')
            .single();
            
          if (newClient) {
            clientId = newClient.id;
            console.log('Created new client:', clientId);
          }
        }
        
        // Update venta with client_id if we have one
        if (clientId) {
          await supabase
            .schema(profile.company_id)
            .from('ventas')
            .update({ cliente_id: clientId })
            .eq('id', ventaId);
        }
      } else {
        console.log('Non-bypass mode: Updating existing client email');
        // For non-bypassed sales: Get the client_id from the venta
        const { data: venta } = await supabase
          .schema(profile.company_id)
          .from('ventas')
          .select('cliente_id')
          .eq('id', ventaId)
          .single();
        
        if (venta?.cliente_id) {
          console.log('Updating email for client:', venta.cliente_id);
          // Update existing client's email field
          await supabase
            .schema(profile.company_id)
            .from('clientes')
            .update({ email: emailInputValue })
            .eq('id', venta.cliente_id);
        } else {
          console.log('No client_id found in venta record:', ventaId);
        }
      }
      
      // In a real implementation, send the actual email here
      // For now, just simulate success
      console.log('Email sent successfully');
      setEmailSent(true);
      
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Error al enviar el email');
    } finally {
      setEmailSending(false);
    }
  };

  const handleFinalizeSale = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Finalizing sale with bypassClientInput:', bypassClientInput);
      
      // Client validation - only if we're not bypassing client input
      if (!bypassClientInput) {
        // If no localSelectedClient, we need to validate the manual inputs
        if (!localSelectedClient) {
          // Check for name
          if (!newClient.nombre) {
            setError('Debe ingresar el nombre/razón social del cliente');
            setLoading(false);
            return;
          }
          
          // Check for CUIT/CUIL format
          if (!newClient.cuil_cuit) {
            setError('Debe ingresar el CUIT/CUIL del cliente');
            setLoading(false);
            return;
          }
          
          // Validate CUIT/CUIL is exactly 11 digits
          if (!/^\d{11}$/.test(newClient.cuil_cuit)) {
            setError('El CUIT/CUIL debe contener exactamente 11 números');
            setLoading(false);
            return;
          }
          
          // Check for address
          if (!newClient.domicilio) {
            setError('Debe ingresar el domicilio del cliente');
            setLoading(false);
            return;
          }
        }
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('No company associated with user');

      let clientId = localSelectedClient?.id;

      // Create client if needed (only if we don't have a selected client)
      if (!clientId && (newClient.nombre || invoiceType !== 'CF')) {
        const client = await handleCreateClient();
        clientId = client.id;
      }

      // Create factura
      const { data: factura, error: facturaError } = await supabase
        .schema(profile.company_id)
        .from('facturas')
        .insert({
           tipo: invoiceType,
        nombre: clientId ? (selectedClient?.nombre || newClient.nombre) : null,
        cuil_cuit: clientId ? (selectedClient?.cuil_cuit || newClient.cuil_cuit) : null,
        domicilio: clientId ? (selectedClient?.domicilio || newClient.domicilio) : null,
      })
        .select()
        .single();

      if (facturaError) throw facturaError;

      // Create venta with total discounts information
      const { data: venta, error: ventaError } = await supabase
        .schema(profile.company_id)
        .from('ventas')
        .insert({
          local_id: localId,
          metodos_de_pago_id: selectedPaymentMethod?.id,
          cliente_id: clientId,
          monto_neto: roundToTwoDecimals(totals.neto),
          monto_iva: roundToTwoDecimals(totals.iva),
          monto_total: roundToTwoDecimals(totals.total),
          total_descuentos: roundToTwoDecimals(totals.totalDiscounts) || null, // Add total discounts from sales summary
          user_id: user.id,
          factura_id: factura.id,
          caja_id: cajaId
        })
        .select()
        .single();

      if (ventaError) throw ventaError;

      // Create ventas_items and stock movements
      for (const item of items) {
        // Calculate total discount for this item (if any)
        const itemDiscountTotal = roundToTwoDecimals(item.discounts?.reduce((total, discount) => 
          total + discount.discountValue, 0
        ) || 0);
        
        // Create venta_item with discount information
        const { error: itemError } = await supabase
          .schema(profile.company_id)
          .from('ventas_items')
          .insert({
            venta_id: venta.id,
            variantes_id: item.variantId,
            cantidad: item.quantity,
            precio_unitario: roundToTwoDecimals(item.price),
            alicuota_iva: roundToTwoDecimals(item.price * 0.21),
            descuentos: itemDiscountTotal !== null && itemDiscountTotal > 0 ? itemDiscountTotal : null, // Set discount value or NULL if no discount
            subtotal: roundToTwoDecimals((item.price * item.quantity) - (itemDiscountTotal || 0)) // Subtract discount from subtotal
          });

        if (itemError) throw itemError;

        // Create stock movement
        const { data: movimientoData, error: movimientoError } = await supabase
          .schema(profile.company_id)
          .from('movimiento_de_stock')
          .insert({
            variante_id: item.variantId,
            tipo: 'salida',
            cantidad: item.quantity,
            motivo: 'venta en PoS',
            local_id_origen: localId
          })
          .select()
          .single();

        if (movimientoError) throw movimientoError;
        
        // Step 1: Update stock quantity based on movimiento_de_stock (deduct from stock)
        // Use a database transaction with row-level locking to handle concurrent updates
        const { error: stockUpdateError } = await supabase.rpc('update_stock_quantity', {
          p_local_id: movimientoData.local_id_origen,
          p_variante_id: movimientoData.variante_id,
          p_quantity: movimientoData.cantidad
        });

        // If the RPC function doesn't exist yet, use a manual update with locking
        if (stockUpdateError) {
          console.log('Falling back to manual stock update:', stockUpdateError);
          
          // Find the stock record and update it
          const { data: stockData, error: stockFetchError } = await supabase
            .schema(profile.company_id)
            .from('stock')
            .select('id, cantidad_disponible')
            .eq('local_id', movimientoData.local_id_origen)
            .eq('variantes_id', movimientoData.variante_id)
            .single();
          
          if (stockFetchError) {
            console.error('Error fetching stock record:', stockFetchError);
            throw new Error(`No stock record found for local_id: ${movimientoData.local_id_origen} and variante_id: ${movimientoData.variante_id}`);
          }
          
          // Update the stock quantity
          const { error: stockUpdateManualError } = await supabase
            .schema(profile.company_id)
            .from('stock')
            .update({ cantidad_disponible: stockData.cantidad_disponible - movimientoData.cantidad })
            .eq('id', stockData.id);
          
          if (stockUpdateManualError) {
            console.error('Error updating stock quantity:', stockUpdateManualError);
            throw stockUpdateManualError;
          }
        }
      }

      // Update factura with venta_id
      const { error: updateFacturaError } = await supabase
        .schema(profile.company_id)
        .from('facturas')
        .update({ venta_id: venta.id })
        .eq('id', factura.id);

      if (updateFacturaError) throw updateFacturaError;
      
      // Step 2: Get payment method details including tiempo_de_pago
      if (!selectedPaymentMethod) {
        throw new Error('No payment method selected');
      }
      
      const { data: paymentMethodData, error: paymentMethodError } = await supabase
        .schema(profile.company_id)
        .from('metodos_de_pago')
        .select('id, metodo_de_pago, tiempo_de_pago')
        .eq('id', selectedPaymentMethod.id)
        .single();
        
      if (paymentMethodError) {
        console.error('Error fetching payment method details:', paymentMethodError);
        throw paymentMethodError;
      }
      
      // Calculate the fecha for movimientos_caja based on tiempo_de_pago
      const currentDate = new Date();
      let fechaMovimiento = new Date(currentDate);
      
      // Add days if tiempo_de_pago is specified
      if (paymentMethodData.tiempo_de_pago) {
        fechaMovimiento.setDate(currentDate.getDate() + paymentMethodData.tiempo_de_pago);
      }
      
      // Step 3: Create entry in movimientos_caja
      const { error: movimientoCajaError } = await supabase
        .schema(profile.company_id)
        .from('movimientos_caja')
        .insert({
          caja_id: cajaId,
          tipo_movimiento: 'Ingreso',
          monto: roundToTwoDecimals(totals.total), // Apply rounding
          descripcion: 'Venta en Punto de Venta',
          metodos_de_pago_id: selectedPaymentMethod.id,
          referencia_id: venta.id,
          fecha: fechaMovimiento.toISOString(),
          usuario_id: user.id
        });
        
      if (movimientoCajaError) {
        console.error('Error creating cash movement entry:', movimientoCajaError);
        throw movimientoCajaError;
      }
      
      // Step 4: Conditionally update cash register balance if tiempo_de_pago is 0 or null
      if (!paymentMethodData.tiempo_de_pago || paymentMethodData.tiempo_de_pago === 0) {
        // Get current cash register balance
        const { data: cajaData, error: cajaFetchError } = await supabase
          .schema(profile.company_id)
          .from('cajas')
          .select('id, saldo_actual')
          .eq('id', cajaId)
          .single();
          
        if (cajaFetchError) {
          console.error('Error fetching cash register data:', cajaFetchError);
          throw cajaFetchError;
        }
        
        // Update cash register balance
        const { error: cajaUpdateError } = await supabase
          .schema(profile.company_id)
          .from('cajas')
          .update({ saldo_actual: roundToTwoDecimals(cajaData.saldo_actual + totals.total) }) // Apply rounding
          .eq('id', cajaId);
          
        if (cajaUpdateError) {
          console.error('Error updating cash register balance:', cajaUpdateError);
          throw cajaUpdateError;
        }
      }

      // Store IDs for success screen functionality
      setVentaId(venta.id);
      setShowSuccessScreen(true);
      
      // Dispatch custom event to update stock in SalesRegister
      const stockUpdateEvent = new CustomEvent('sale-stock-update', { 
        detail: { 
          items: items.map(item => ({
            variantId: item.variantId,
            quantity: item.quantity
          }))
        } 
      });
      window.dispatchEvent(stockUpdateEvent);
      // Processing is complete, though we still show success screen
    } catch (err) {
      console.error('Error finalizing sale:', err);
      setError(err instanceof Error ? err.message : 'Error finalizing sale');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Show the processing screen immediately when bypass conditions are met
  if (bypassClientInput && !showSuccessScreen) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 flex flex-col items-center justify-center">
          <div className="mb-4">
            <Loader2 className="w-12 h-12 animate-spin text-blue-500" />
          </div>
          <h3 className="text-xl font-medium text-gray-900">
            Procesando venta...
          </h3>
          <p className="text-gray-500 mt-2 text-center">
            Estamos finalizando tu venta automáticamente
          </p>
        </div>
      </div>
    );
  }

  if (showSuccessScreen) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-xl font-medium text-gray-900">
              ¡Venta finalizada con éxito!
            </h3>
            <button
              onClick={onSuccess}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-6">
          
          {/* Email section */}
          <div className="mb-6 space-y-4 border p-4 rounded-lg border-gray-200">
            <h4 className="font-medium">Enviar factura por mail</h4>
            
            {emailSent ? (
              <div className="flex flex-col items-center py-4">
                <CheckCircle className="w-12 h-12 text-green-500 mb-3" />
                <p className="text-center text-gray-700">¡Email enviado con éxito!</p>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email*
                  </label>
                  <input
                    type="email"
                    value={emailInputValue}
                    onChange={handleEmailChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      emailInputValue && !isEmailValid ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  />
                  {emailInputValue && !isEmailValid && (
                    <p className="mt-1 text-sm text-red-600">Ingrese un email válido</p>
                  )}
                </div>
                
                {/* Only show name field in bypass mode */}
                {bypassClientInput && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre y Apellido (opcional)
                    </label>
                    <input
                      type="text"
                      value={nameInputValue}
                      onChange={(e) => setNameInputValue(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                )}
                
                <button
                  onClick={handleEmailSend}
                  disabled={!isEmailValid || emailSending}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    isEmailValid ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {emailSending ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Mail className="w-5 h-5" />
                  )}
                  Enviar factura
                </button>
              </>
            )}
          </div>
          
          <div className="space-y-4">
            <button
              onClick={onSuccess}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
              Realizar otra venta
            </button>
          </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-medium text-gray-900">
            Finalizar Venta
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* Invoice type selection has been moved to SalesSummary component */}

          {invoiceType === 'A' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Condición IVA
                </label>
                <select
                  value={selectedCondicionIva}
                  onChange={(e) => setSelectedCondicionIva(e.target.value as 'RI' | 'Exento')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="RI">Responsable Inscripto</option>
                  <option value="Exento">Exento</option>
                </select>
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Razón Social/Nombre
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowClientSearchModal(true)}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <UserSearch className="w-4 h-4" />
                    Buscar cliente existente
                  </button>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    value={localSelectedClient ? localSelectedClient.nombre : clientSearch}
                    onChange={(e) => {
                      setClientSearch(e.target.value);
                      setNewClient({ ...newClient, nombre: e.target.value });
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CUIT
                </label>
                <input
                  type="text"
                  value={localSelectedClient ? localSelectedClient.cuil_cuit : newClient.cuil_cuit || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 11);
                    setNewClient({ ...newClient, cuil_cuit: value });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  pattern="[0-9]{11}"
                  title="El CUIT/CUIL debe contener exactamente 11 números"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Domicilio
                </label>
                <input
                  type="text"
                  value={localSelectedClient ? localSelectedClient.domicilio : newClient.domicilio || ''}
                  onChange={(e) => setNewClient({ ...newClient, domicilio: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Email field removed - now only in success screen */}
            </div>
          )}

          {invoiceType === 'B' && (
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Apellido y Nombre
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowClientSearchModal(true)}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <UserSearch className="w-4 h-4" />
                    Buscar cliente existente
                  </button>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    value={localSelectedClient ? localSelectedClient.nombre : clientSearch}
                    onChange={(e) => {
                      setClientSearch(e.target.value);
                      setNewClient({ ...newClient, nombre: e.target.value });
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CUIT
                </label>
                <input
                  type="text"
                  value={localSelectedClient ? localSelectedClient.cuil_cuit : newClient.cuil_cuit || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 11);
                    setNewClient({ ...newClient, cuil_cuit: value });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  pattern="[0-9]{11}"
                  title="El CUIT/CUIL debe contener exactamente 11 números"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Domicilio
                </label>
                <input
                  type="text"
                  value={localSelectedClient ? localSelectedClient.domicilio : newClient.domicilio || ''}
                  onChange={(e) => setNewClient({ ...newClient, domicilio: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Email field removed - now only in success screen */}
            </div>
          )}

          {invoiceType === 'CF' && (
            <div className="space-y-4">
              {selectedPaymentMethod?.metodo_de_pago === 'Efectivo' &&
               totals.total < (companyConfig?.monto_min_facturacion_cash || 0) ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Apellido y Nombre (opcional)
                    </label>
                    <input
                      type="text"
                      value={newClient.nombre || ''}
                      onChange={(e) => setNewClient({ ...newClient, nombre: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email (opcional)
                    </label>
                    <input
                      type="email"
                      value={newClient.email || ''}
                      onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={handleFinalizeSale}
                      className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Omitir y completar venta
                    </button>
                    <button
                      onClick={handleFinalizeSale}
                      disabled={!newClient.nombre}
                      className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50"
                    >
                      Agregar Cliente y completar venta
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Apellido y Nombre
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowClientSearchModal(true)}
                        className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <UserSearch className="w-4 h-4" />
                        Buscar cliente existente
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        value={localSelectedClient ? localSelectedClient.nombre : clientSearch}
                        onChange={(e) => {
                          setClientSearch(e.target.value);
                          setNewClient({ ...newClient, nombre: e.target.value });
                        }}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                      <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CUIL
                    </label>
                    <input
                      type="text"
                      value={localSelectedClient ? localSelectedClient.cuil_cuit : newClient.cuil_cuit || ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 11);
                        setNewClient({ ...newClient, cuil_cuit: value });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      pattern="[0-9]{11}"
                      title="El CUIT/CUIL debe contener exactamente 11 números"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Domicilio
                    </label>
                    <input
                      type="text"
                      value={localSelectedClient ? localSelectedClient.domicilio : newClient.domicilio || ''}
                      onChange={(e) => setNewClient({ ...newClient, domicilio: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email (opcional)
                    </label>
                    <input
                      type="email"
                      value={localSelectedClient ? localSelectedClient.email : newClient.email || ''}
                      onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Client Search Results (Inline) */}
          {clients.length > 0 && !localSelectedClient && !showClientSearchModal && (
            <div className="mt-2 border rounded-lg shadow-sm">
              {clients.map((client) => (
                <button
                  key={client.id}
                  onClick={() => {
                    setLocalSelectedClient(client);
                    setClients([]);
                    setClientSearch('');
                    setNewClient({
                      nombre: client.nombre,
                      cuil_cuit: client.cuil_cuit,
                      domicilio: client.domicilio || '',
                      email: client.email || ''
                    });
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                >
                  <div className="font-medium">{client.nombre}</div>
                  <div className="text-sm text-gray-500">
                    CUIT/CUIL: {client.cuil_cuit}
                  </div>
                </button>
              ))}
            </div>
          )}
          
          {/* Client Search Modal */}
          {showClientSearchModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
              <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between p-4 border-b">
                  <h3 className="text-lg font-medium text-gray-900">
                    Buscar cliente existente
                  </h3>
                  <button
                    onClick={() => {
                      setShowClientSearchModal(false);
                      setClients([]);
                      setClientSearch('');
                    }}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                
                <div className="p-4 border-b">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Buscar por nombre o CUIT/CUIL"
                      value={clientSearch}
                      onChange={(e) => {
                        setClientSearch(e.target.value);
                        searchClients(e.target.value, true);
                      }}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  </div>
                </div>
                
                <div className="overflow-y-auto flex-grow">
                  {clients.length === 0 ? (
                    <div className="p-6 text-center text-gray-500">
                      No se encontraron clientes
                    </div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Nombre
                          </th>
                          <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            CUIL/CUIT
                          </th>
                          <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Domicilio
                          </th>
                          <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {clients.map((client) => (
                          <tr 
                            key={client.id} 
                            className="hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={() => {
                              setLocalSelectedClient(client);
                              setShowClientSearchModal(false);
                              setClients([]);
                              setClientSearch('');
                              setNewClient({
                                nombre: client.nombre,
                                cuil_cuit: client.cuil_cuit,
                                domicilio: client.domicilio || '',
                                email: client.email || ''
                              });
                            }}
                          >
                            <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              {client.nombre}
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                              {client.cuil_cuit}
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                              {client.domicilio || '-'}
                            </td>
                            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
                              {client.email || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                
                <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
                  <button
                    onClick={() => {
                      setShowClientSearchModal(false);
                      setClients([]);
                      setClientSearch('');
                    }}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancelar
          </button>
          {invoiceType && (
            <button
              onClick={handleFinalizeSale}
              disabled={loading}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Procesando...
                </>
              ) : (
                'Completar Venta'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};